import React, {useEffect, useState} from 'react';
import {Button, Space} from 'antd';
import {
    ArrowsClockwiseIcon,
    ArrowsInIcon,
    ArrowsOutIcon,
    BoundingBoxIcon,
    CubeIcon,
    PencilIcon,
    PersonArmsSpreadIcon,
    PerspectiveIcon,
    RulerIcon,
    XIcon
} from '@phosphor-icons/react';
import {useCurrentUser} from '../../../../contexts/UserContext';
import {useViewerStore} from "../../../../store/store";
import {registerRef, setPathname} from "../../../../helpers/storeHelpers";
import { EditorIconTooltip } from '../EditorIconTooltip';

const Toolbar = () => {

    const pathname = useViewerStore(state => state.pathname)
    const sceneViewer = useViewerStore(state => state.sceneViewer);

    const {user, hasCapability} = useCurrentUser();

    const isStreetViewVisible = useViewerStore((state) => state.isStreetViewVisible);
    const isTapeMeasureActive = useViewerStore((state) => state.isTapeMeasureActive);

    const [isOpen, setIsOpen] = useState(true);
    const [rotateActive, setRotateActive] = useState(false);
    const [isLayoutFullScreen, setIsLayoutFullScreen] = useState(
        () => typeof document !== 'undefined' && document.body.classList.contains('full-screen'),
    );
    const is2DModeActive = useViewerStore(state => state.is2DModeActive);
    const is3DModeActive = useViewerStore(state => state.is3DModeActive);

    //Handlers

    const setViewTo2DHandler = () => {
        sceneViewer.viewMode = '2d';

        useViewerStore.setState({
            is2DModeActive: true,
            is3DModeActive: false,
        })

        sceneViewer.setAutoRotate(false);
        sceneViewer.setCameraView('2d');
    }

    const setViewTo3DHandler = () => {
        useViewerStore.setState({
            is2DModeActive: false,
            is3DModeActive: true,
        });

        sceneViewer.viewMode = '3d';
        sceneViewer.setCameraView('3d')
    }

    const autoRotate = () => {
        setRotateActive((rotateActive) => !rotateActive);
        sceneViewer.setAutoRotate(!rotateActive);
    }

    /** Resize 3D canvas after layout chrome changes (header / bottom panel hide). */
    const resizeViewerAfterLayout = () => {
        const viewer = useViewerStore.getState().sceneViewer;
        requestAnimationFrame(() => {
            viewer?.resizeCanvas?.();
            setTimeout(() => useViewerStore.getState().sceneViewer?.resizeCanvas?.(), 320);
        });
    };

    const enterLayoutFullScreen = () => {
        document.body.classList.add('full-screen');
        setIsLayoutFullScreen(true);
        useViewerStore.setState({ isRightSidebarOpen: false });

        // Native FS is unreliable on iOS; layout class is the source of truth on mobile/tablet.
        const useNativeFs = !document.body.classList.contains('mobile-bottom-layout');
        if (useNativeFs && !document.fullscreenElement) {
            const root = document.documentElement as HTMLElement & {
                webkitRequestFullscreen?: () => Promise<void> | void;
            };
            const requestFs = root.requestFullscreen?.bind(root) ?? root.webkitRequestFullscreen?.bind(root);
            try {
                void requestFs?.();
            } catch {
                /* layout-only fullscreen still applies */
            }
        }

        resizeViewerAfterLayout();
    };

    const exitLayoutFullScreen = () => {
        document.body.classList.remove('full-screen');
        setIsLayoutFullScreen(false);
        useViewerStore.setState({ isRightSidebarOpen: true });

        if (document.fullscreenElement && document.exitFullscreen) {
            void document.exitFullscreen().catch(() => undefined);
        }

        resizeViewerAfterLayout();
    };

    const fullScreenHandler = () => {
        if (document.body.classList.contains('full-screen') || document.fullscreenElement) {
            exitLayoutFullScreen();
        } else {
            enterLayoutFullScreen();
        }
    };

    useEffect(() => {
        const onFullscreenChange = () => {
            if (!document.fullscreenElement && document.body.classList.contains('full-screen')) {
                // Keep layout FS on mobile/tablet (native API was never the source of truth).
                if (!document.body.classList.contains('mobile-bottom-layout')) {
                    document.body.classList.remove('full-screen');
                    setIsLayoutFullScreen(false);
                    useViewerStore.setState({ isRightSidebarOpen: true });
                    resizeViewerAfterLayout();
                }
            }
        };

        document.addEventListener('fullscreenchange', onFullscreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', onFullscreenChange);
            document.body.classList.remove('full-screen');
        };
    }, []);

    const streetViewHandler = () => {
        useViewerStore.setState({isStreetViewVisible: true, isRightSidebarOpen: false});
        sceneViewer.loadStreetView();
    }
    const topViewHandler = () => {
        sceneViewer.setTopView(true);
    }

    const measurementRedirectHandler = () => {
        if (!pathname.includes('tape-measure')) {
            useViewerStore.setState({isTapeMeasureActive: true})
            setPathname('/configure/tape-measure')
        } else {
            useViewerStore.setState({isTapeMeasureActive: false})
            setPathname('/configure/roof')
        }
    }


    useEffect(() => {
        if (user && !pathname.includes('tape-measure')) {
            useViewerStore.setState({isTapeMeasureActive: false})
        }
    }, [pathname, user]);

    // useEffect(() => {
    //
    //     if(sceneViewer==null) return;
    //
    //     if(is2DModeActive){
    //         sceneViewer.viewerAlert({
    //             title: '2D View',
    //             message: "",
    //             messageType: 'info',
    //             isToast: true
    //     })
    //
    //
    //         setTimeout(sceneViewer.removeViewerAlert, 1500)
    //     }
    //     else {
    //         sceneViewer.viewerAlert({
    //             title: '3D View',
    //             message: "",
    //             messageType: 'info',
    //             isToast: true
    //         })
    //
    //         setTimeout(sceneViewer.removeViewerAlert, 1500)
    //
    //     }
    //
    //     return()=>{
    //         clearTimeout(sceneViewer.removeViewerAlert)
    //
    //     }
    //
    // }, [is2DModeActive,is3DModeActive,sceneViewer]);

    return (
        <>
            <div className='toolbar toolbar--canvas'>
                <Space.Compact className="toolbar-btn-group" id="CanvasToolbar">
                    <Button
                        className={!isOpen ? 'editor-toolbar-toggle editor-toolbar-toggle--close' : 'editor-toolbar-toggle'}
                        type='default'
                        size='large'
                        icon={
                            <EditorIconTooltip title={!isOpen ? 'Close Toolbar' : 'Open Toolbar'} zone="toolbar">
                                <span className="editor-icon-tooltip__hit">
                                    {isOpen ? <PencilIcon size={24} weight='regular'/> : <XIcon size={24} weight='bold'/>}
                                </span>
                            </EditorIconTooltip>
                        }
                        onClick={() => setIsOpen(!isOpen)}
                    />
                    {!isOpen && (
                        <>
                        {(hasCapability('Google_Streetview')) && (
                            <Button
                                type={isStreetViewVisible ? 'primary' : 'default'}
                                size='large'
                                icon={
                                    <EditorIconTooltip title="Street View" zone="toolbar">
                                        <span className="editor-icon-tooltip__hit">
                                            <PersonArmsSpreadIcon size={22} weight='regular'/>
                                        </span>
                                    </EditorIconTooltip>
                                }
                                onClick={streetViewHandler}
                                id='streetViewTool'
                            />
                        )}
                        {hasCapability('Tape_Measure') && (
                            <Button
                                ref={registerRef("btnMeasure")}
                                type={isTapeMeasureActive ? 'primary' : 'default'}
                                size='large'
                                icon={
                                    <EditorIconTooltip title="Tape Measure" zone="toolbar">
                                        <span className="editor-icon-tooltip__hit">
                                            <RulerIcon size={22} weight='regular'/>
                                        </span>
                                    </EditorIconTooltip>
                                }
                                onClick={measurementRedirectHandler}
                            />
                        )}
                        {hasCapability('360_View') && (
                            <Button
                                type={rotateActive ? 'primary' : 'default'}
                                size='large'
                                icon={
                                    <EditorIconTooltip title="360 View" zone="toolbar">
                                        <span className="editor-icon-tooltip__hit">
                                            <ArrowsClockwiseIcon size={22} weight='regular'/>
                                        </span>
                                    </EditorIconTooltip>
                                }
                                id='btnAutoRotate'
                                onClick={autoRotate}
                            />
                        )}
                        {hasCapability('Top_View') && (
                            <Button
                                type={'default'}
                                size='large'
                                icon={
                                    <EditorIconTooltip title="Top View" zone="toolbar">
                                        <span className="editor-icon-tooltip__hit">
                                            <BoundingBoxIcon size={22} weight='regular'/>
                                        </span>
                                    </EditorIconTooltip>
                                }
                                id='btnTopView'
                                onClick={topViewHandler}
                            />
                        )}

                        <Button
                            type={isLayoutFullScreen ? 'primary' : 'default'}
                            size='large'
                            icon={
                                <EditorIconTooltip title={isLayoutFullScreen ? 'Exit Full Screen' : 'Full Screen'} zone="toolbar">
                                    <span className="editor-icon-tooltip__hit">
                                        {isLayoutFullScreen
                                            ? <ArrowsInIcon size={22} weight='regular'/>
                                            : <ArrowsOutIcon size={22} weight='regular'/>}
                                    </span>
                                </EditorIconTooltip>
                            }
                            id='fullScreen'
                            onClick={fullScreenHandler}
                        />
                        {hasCapability('2D_View') && (
                            <Button
                                type={is2DModeActive ? 'primary' : 'default'}
                                size='large'
                                icon={
                                    <EditorIconTooltip title="2D View" zone="toolbar">
                                        <span className="editor-icon-tooltip__hit">
                                            <PerspectiveIcon size={22} weight='regular'/>
                                        </span>
                                    </EditorIconTooltip>
                                }
                                id='2DViewBtn'
                                onClick={setViewTo2DHandler}
                            />
                        )}
                        {hasCapability('3D_View') && (
                            <Button
                                type={is3DModeActive ? 'primary' : 'default'}
                                size='large'
                                icon={
                                    <EditorIconTooltip title="3D View" zone="toolbar">
                                        <span className="editor-icon-tooltip__hit">
                                            <CubeIcon size={22} weight='regular'/>
                                        </span>
                                    </EditorIconTooltip>
                                }
                                id='3DViewBtn'
                                onClick={setViewTo3DHandler}
                            />
                        )}
                        </>
                    )}
                </Space.Compact>
            </div>
        </>
    );
}

export default Toolbar;
